import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { 
    Loader2, 
    AlertCircle,
    CheckCircle2,
    KeyRound,
    Building2,
    Search,
    User
} from 'lucide-react';
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
    listUserAccessGrants,
    grantOrganizationAccess,
    revokeOrganizationAccess
} from "@/api/root-admin/root-admin.api";
import { getOrganizations } from "@/api/users/users.api";
import type { RootUserListItem, AccessGrant } from "@/api/root-admin/root-admin.types";
import { cn } from "@/lib/utils";

interface Organization {
    id: string;
    name: string;
}

interface ManageAccessDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    user: RootUserListItem | null;
    onSuccess?: () => void;
}

export function ManageAccessDialog({
    open,
    onOpenChange,
    user,
    onSuccess,
}: ManageAccessDialogProps) {
    const { error, success } = useEnhancedToast();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [currentGrants, setCurrentGrants] = useState<AccessGrant[]>([]);
    const [selectedOrgs, setSelectedOrgs] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");

    const errorRef = useRef(error);
    const successRef = useRef(success);
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    const fetchData = useCallback(async () => {
        if (!user) return;

        try {
            setIsLoading(true);
            
            // Fetch organizations and current grants in parallel
            const [orgsRes, grantsRes] = await Promise.all([
                getOrganizations(),
                listUserAccessGrants(user.id)
            ]);

            if (orgsRes.data) {
                // The API returns an array of organization objects with organization_id and organization_name
                const orgs = Array.isArray(orgsRes.data) ? orgsRes.data : [];
                // Filter out any orgs without valid id or name, and map to our format
                setOrganizations(
                    orgs
                        .filter((org: any) => org && (org.organization_id || org.id || org._id) && (org.organization_name || org.name))
                        .map((org: { organization_id?: string; id?: string; _id?: string; organization_name?: string; name?: string }) => ({ 
                            id: org.organization_id || org.id || org._id || '', 
                            name: org.organization_name || org.name || ''
                        }))
                );
            }

            if (grantsRes.data) {
                setCurrentGrants(grantsRes.data.grants);
                // Initialize selected orgs from current grants
                setSelectedOrgs(new Set(grantsRes.data.grants.map(g => g.organization_id)));
            }

        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Error Loading Data",
                description: err instanceof Error ? err.message : "Could not load data",
                duration: 4000,
            });
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (open && user) {
            fetchData();
            setSearchQuery("");
        }
    }, [open, user, fetchData]);

    const handleOrgToggle = (orgId: string, checked: boolean) => {
        setSelectedOrgs(prev => {
            const next = new Set(prev);
            if (checked) {
                next.add(orgId);
            } else {
                next.delete(orgId);
            }
            return next;
        });
    };

    const handleSave = async () => {
        if (!user) return;

        setIsSaving(true);
        try {
            const currentGrantIds = new Set(currentGrants.map(g => g.organization_id));
            
            // Find orgs to add (selected but not in current grants)
            const orgsToAdd = [...selectedOrgs].filter(id => !currentGrantIds.has(id));
            
            // Find orgs to remove (in current grants but not selected)
            const orgsToRemove = currentGrants
                .filter(g => !selectedOrgs.has(g.organization_id))
                .map(g => g.organization_id);

            // Grant access to new orgs
            if (orgsToAdd.length > 0) {
                const grantResult = await grantOrganizationAccess(user.id, {
                    organization_ids: orgsToAdd
                });
                if (grantResult.error) {
                    throw new Error(grantResult.error);
                }
            }

            // Revoke access from removed orgs
            for (const orgId of orgsToRemove) {
                const revokeResult = await revokeOrganizationAccess(user.id, orgId);
                if (revokeResult.error) {
                    throw new Error(revokeResult.error);
                }
            }

            const changes = orgsToAdd.length + orgsToRemove.length;
            if (changes > 0) {
                successRef.current({
                    icon: CheckCircle2,
                    title: "Access Updated",
                    description: `Organization access updated for ${user.email}`,
                    duration: 4000,
                });
            }

            onOpenChange(false);
            onSuccess?.();
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Update Failed",
                description: err instanceof Error ? err.message : "Could not update access",
                duration: 4000,
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Get the initial grant IDs for stable sorting (so items don't jump when toggling)
    const initialGrantIds = new Set(currentGrants.map(g => g.organization_id));
    
    // Filter and sort organizations - initially granted ones appear first, then alphabetically
    const filteredOrgs = organizations
        .filter(org => org.name?.toLowerCase().includes(searchQuery.toLowerCase()))
        .sort((a, b) => {
            const aGranted = initialGrantIds.has(a.id);
            const bGranted = initialGrantIds.has(b.id);
            // Initially granted items come first
            if (aGranted && !bGranted) return -1;
            if (!aGranted && bGranted) return 1;
            // Then sort alphabetically by name
            return (a.name || '').localeCompare(b.name || '');
        });

    const hasChanges = () => {
        const currentGrantIds = new Set(currentGrants.map(g => g.organization_id));
        if (selectedOrgs.size !== currentGrantIds.size) return true;
        for (const id of selectedOrgs) {
            if (!currentGrantIds.has(id)) return true;
        }
        return false;
    };

    if (!user) return null;

    const hasSignedUp = user.first_name && user.last_name;
    const initials = hasSignedUp 
        ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
        : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col gap-0 p-0">
                <DialogHeader className="border-b bg-muted/20 px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <KeyRound className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <DialogTitle className="text-base font-semibold">
                                Manage Organization Access
                            </DialogTitle>
                            <DialogDescription className="text-xs text-muted-foreground">
                                Select which organizations this user can access
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="px-6 py-5 space-y-4 flex-1 overflow-auto">
                {/* User Info */}
                <div className="rounded-lg border bg-muted/50 p-4">
                    <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                            <AvatarFallback className={cn(
                                "text-sm font-semibold",
                                hasSignedUp 
                                    ? "bg-primary/10 text-primary" 
                                    : "bg-muted text-muted-foreground"
                            )}>
                                {hasSignedUp ? initials : <User className="h-4 w-4" />}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                            {hasSignedUp ? (
                                <p className="font-medium truncate">
                                    {user.first_name} {user.last_name}
                                </p>
                            ) : (
                                <p className="text-sm italic text-muted-foreground">
                                    Pending signup
                                </p>
                            )}
                            <p className="text-sm text-muted-foreground truncate">
                                {user.email}
                            </p>
                        </div>
                        <Badge variant="secondary" className="capitalize">
                            {user.role}
                        </Badge>
                    </div>
                </div>

                {/* Search and Select/Deselect All Toggle */}
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                            placeholder="Search organizations..."
                            className="pl-9"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    {(() => {
                        // Check if all filtered orgs are selected
                        const allFilteredSelected = filteredOrgs.length > 0 && 
                            filteredOrgs.every(org => selectedOrgs.has(org.id));
                        
                        return (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    if (allFilteredSelected) {
                                        // Deselect all filtered orgs
                                        const filteredIds = new Set(filteredOrgs.map(org => org.id));
                                        setSelectedOrgs(prev => {
                                            const next = new Set(prev);
                                            filteredIds.forEach(id => next.delete(id));
                                            return next;
                                        });
                                    } else {
                                        // Select all filtered orgs
                                        const allOrgIds = new Set(filteredOrgs.map(org => org.id));
                                        setSelectedOrgs(prev => {
                                            const next = new Set(prev);
                                            allOrgIds.forEach(id => next.add(id));
                                            return next;
                                        });
                                    }
                                }}
                                disabled={isLoading || filteredOrgs.length === 0}
                                className="text-xs px-3 whitespace-nowrap"
                            >
                                {allFilteredSelected ? "Deselect All" : "Select All"}
                            </Button>
                        );
                    })()}
                </div>

                {/* Organizations List */}
                <div className="flex-1 min-h-0">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-[200px]">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : filteredOrgs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[200px] text-center">
                            <Building2 className="h-8 w-8 text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">
                                {searchQuery ? "No organizations match your search" : "No organizations available"}
                            </p>
                        </div>
                    ) : (
                        <ScrollArea className="h-[250px] rounded-md border">
                            <div className="p-2 space-y-1">
                                {filteredOrgs.map((org) => {
                                    const isSelected = selectedOrgs.has(org.id);
                                    return (
                                        <div
                                            key={org.id}
                                            className={cn(
                                                "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                                                isSelected 
                                                    ? "bg-primary/10 border border-primary/20" 
                                                    : "hover:bg-muted/50 border border-transparent"
                                            )}
                                            onClick={() => handleOrgToggle(org.id, !isSelected)}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={(checked) => handleOrgToggle(org.id, checked as boolean)}
                                            />
                                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-background border">
                                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                            <span className="flex-1 font-medium text-sm">{org.name}</span>
                                            {isSelected && (
                                                <CheckCircle2 className="h-4 w-4 text-primary" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </ScrollArea>
                    )}
                </div>

                {/* Summary */}
                <div className="text-sm text-muted-foreground">
                    {selectedOrgs.size} organization{selectedOrgs.size !== 1 ? 's' : ''} selected
                </div>
                </div>

                <DialogFooter className="border-t bg-muted/20 px-6 py-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSave} 
                        disabled={isSaving || !hasChanges()}
                        className="gap-2"
                    >
                        {isSaving ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <CheckCircle2 className="h-4 w-4" />
                                Save Changes
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
